import React, {useEffect, useState} from 'react';
import Layout from '../components/Layout';
import {api} from '../../api';

export default function Settings() {
  const [stages, setStages] = useState([]);
  const [stageForm, setStageForm] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', body: '' });
  const [stageEmails, setStageEmails] = useState([]);

  const loadStages = () => api('/ats/pipeline-stages').then(r => r.ok ? r.json() : []).then(setStages).catch(() => {});
  const loadTemplates = () => api('/ats/email-templates').then(r => r.ok ? r.json() : []).then(setTemplates).catch(() => {});
  const loadStageEmails = () => api('/ats/stage-emails').then(r => r.ok ? r.json() : []).then(setStageEmails).catch(() => {});
  useEffect(() => { loadStages(); loadTemplates(); loadStageEmails(); }, []);

  const handleStageSubmit = async (e) => {
    e.preventDefault();
    if (stageForm.id) {
      await api(`/ats/pipeline-stages/${stageForm.id}`, { method: 'PUT', body: JSON.stringify(stageForm) });
    } else {
      await api('/ats/pipeline-stages', { method: 'POST', body: JSON.stringify(stageForm) });
    }
    setStageForm(null); loadStages();
  };

  const deleteStage = async (id) => { await api(`/ats/pipeline-stages/${id}`, { method: 'DELETE' }); loadStages(); };

  const moveStage = async (idx, dir) => {
    const ids = [...stages].map(s => s.id);
    const [removed] = ids.splice(idx, 1);
    ids.splice(idx + dir, 0, removed);
    await api('/ats/pipeline-stages/reorder', { method: 'POST', body: JSON.stringify({ stageIds: ids }) });
    loadStages();
  };

  const resetForm = () => { setForm({ name: '', subject: '', body: '' }); setEditing(null); setShowForm(false); };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) { await api(`/ats/email-templates/${editing}`, { method: 'PUT', body: JSON.stringify(form) }); }
    else { await api('/ats/email-templates', { method: 'POST', body: JSON.stringify(form) }); }
    resetForm(); loadTemplates();
  };
  const handleEdit = (t) => { setForm({ name: t.name, subject: t.subject, body: t.body }); setEditing(t.id); setShowForm(true); };
  const handleDelete = async (id) => { await api(`/ats/email-templates/${id}`, { method: 'DELETE' }); loadTemplates(); };

  const input = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600";

  return (
    <Layout>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">ATS Settings</h2>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Pipeline Stages</h3>
          <button onClick={() => setStageForm({ name: '', isTerminal: false })} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">+ Add Stage</button>
        </div>

        {stageForm && (
          <form onSubmit={handleStageSubmit} className="p-4 border border-gray-200 rounded-lg mb-4 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Stage Name *</label>
                <input required value={stageForm.name} onChange={e => setStageForm({...stageForm, name: e.target.value})} className={input} />
              </div>
              <div className="flex items-end gap-2 pb-1">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={stageForm.isTerminal || false} onChange={e => setStageForm({...stageForm, isTerminal: e.target.checked})} className="rounded border-gray-300 text-teal-600" />
                  Terminal stage (hired/rejected)
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">{stageForm.id ? 'Save' : 'Add'}</button>
              <button type="button" onClick={() => setStageForm(null)} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
            </div>
          </form>
        )}

        <div className="space-y-2">
          {stages.map((stage, idx) => (
            <div key={stage.id || stage.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-0.5">
                  <button disabled={idx === 0} onClick={() => moveStage(idx, -1)} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs">▲</button>
                  <button disabled={idx === stages.length - 1} onClick={() => moveStage(idx, 1)} className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs">▼</button>
                </div>
                <span className="text-sm font-medium text-gray-900">{stage.name}</span>
                {stage.isTerminal && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Terminal</span>}
              </div>
              {stage.id && (
                <div className="flex gap-2">
                  <button onClick={() => setStageForm({ id: stage.id, name: stage.name, isTerminal: stage.isTerminal })} className="text-sm text-gray-600 hover:text-teal-600">Edit</button>
                  <button onClick={() => deleteStage(stage.id)} className="text-sm text-gray-600 hover:text-red-600">Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Stage Emails</h3>
        <p className="text-xs text-gray-500 mb-4">Automatically send an email when an applicant moves to a stage.</p>
        <div className="space-y-3">
          {stages.filter(s => s.id).map(stage => {
            const mapping = stageEmails.find(se => se.stage === stage.name.toLowerCase());
            return (
              <div key={stage.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-900">{stage.name}</span>
                <div className="flex items-center gap-2">
                  <select value={mapping?.templateId || ''} onChange={async (e) => {
                    if (e.target.value) {
                      await api('/ats/stage-emails', {method: 'POST', body: JSON.stringify({stage: stage.name.toLowerCase(), templateId: e.target.value})});
                    } else if (mapping) {
                      await api(`/ats/stage-emails/${mapping.id}`, {method: 'DELETE'});
                    }
                    loadStageEmails();
                  }} className="px-2 py-1 border border-gray-300 rounded text-sm">
                    <option value="">No email</option>
                    {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm font-medium">+ New Template</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Use variables: {'{{firstName}}'}, {'{{lastName}}'}, {'{{jobTitle}}'}, {'{{companyName}}'}</p>

        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 border border-gray-200 rounded-lg mb-4 space-y-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Template Name *</label><input required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={input} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Subject *</label><input required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className={input} /></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Body *</label><textarea required rows={6} value={form.body} onChange={e => setForm({...form, body: e.target.value})} className={input} /></div>
            <div className="flex gap-2">
              <button type="submit" className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 text-sm">{editing ? 'Save' : 'Create'}</button>
              <button type="button" onClick={resetForm} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm">Cancel</button>
            </div>
          </form>
        )}

        <div className="space-y-3">
          {templates.map(t => (
            <div key={t.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-500">Subject: {t.subject}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(t)} className="text-sm text-gray-600 hover:text-teal-600">Edit</button>
                  <button onClick={() => handleDelete(t.id)} className="text-sm text-gray-600 hover:text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
          {templates.length === 0 && <p className="text-sm text-gray-500">No email templates yet.</p>}
        </div>
      </div>
    </Layout>
  );
}
